import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";
import type { Editor } from "@tiptap/react";

type TextInputElement = HTMLTextAreaElement | HTMLInputElement;

interface StudioFormattingContextValue {
  activeTextarea: TextInputElement | null;
  activeTiptap: Editor | null;
  getOnTextChange: () => ((newText: string) => void) | null;
  registerTextarea: (el: TextInputElement, onChange: (newText: string) => void) => void;
  unregisterTextarea: (el: TextInputElement) => void;
  registerTiptap: (editor: Editor) => void;
  unregisterTiptap: (editor: Editor) => void;
}

const StudioFormattingContext = createContext<StudioFormattingContextValue | null>(null);

function StudioFormattingProvider({ children }: { children: ReactNode }) {
  const [activeTextarea, setActiveTextarea] = useState<TextInputElement | null>(null);
  const [activeTiptap, setActiveTiptap] = useState<Editor | null>(null);
  const onTextChangeRef = useRef<((newText: string) => void) | null>(null);
  const activeTextareaRef = useRef<TextInputElement | null>(null);
  const activeTiptapRef = useRef<Editor | null>(null);

  const registerTextarea = useCallback((el: TextInputElement, onChange: (newText: string) => void) => {
    activeTextareaRef.current = el;
    onTextChangeRef.current = onChange;
    setActiveTextarea(el);
    // Clear TipTap when a textarea takes focus
    activeTiptapRef.current = null;
    setActiveTiptap(null);
  }, []);

  const unregisterTextarea = useCallback((el: TextInputElement) => {
    if (activeTextareaRef.current === el) {
      setTimeout(() => {
        if (activeTextareaRef.current === el) {
          activeTextareaRef.current = null;
          onTextChangeRef.current = null;
          setActiveTextarea(null);
        }
      }, 200);
    }
  }, []);

  const registerTiptap = useCallback((editor: Editor) => {
    activeTiptapRef.current = editor;
    setActiveTiptap(editor);
    // Clear textarea when TipTap takes focus
    activeTextareaRef.current = null;
    onTextChangeRef.current = null;
    setActiveTextarea(null);
  }, []);

  const unregisterTiptap = useCallback((editor: Editor) => {
    if (activeTiptapRef.current === editor) {
      setTimeout(() => {
        if (activeTiptapRef.current === editor) {
          activeTiptapRef.current = null;
          setActiveTiptap(null);
        }
      }, 200);
    }
  }, []);

  const getOnTextChange = useCallback(() => onTextChangeRef.current, []);

  return (
    <StudioFormattingContext.Provider
      value={{
        activeTextarea,
        activeTiptap,
        getOnTextChange,
        registerTextarea,
        unregisterTextarea,
        registerTiptap,
        unregisterTiptap,
      }}
    >
      {children}
    </StudioFormattingContext.Provider>
  );
}

function useStudioFormatting() {
  const ctx = useContext(StudioFormattingContext);
  if (!ctx) {
    throw new Error("useStudioFormatting must be used within StudioFormattingProvider");
  }
  return ctx;
}

export { StudioFormattingProvider, useStudioFormatting, StudioFormattingContext };
